/**
 * Policy templates for the Templates page and "Save as template".
 *
 * Same idiom as `state/policies.ts`: a per-org cached atom over the workspace
 * server's `/api/amoreg/policy-templates`, which forwards to the domain API
 * where the tenant guard lives. A template file is downloaded through the
 * server, never from its storage URL.
 *
 * @module state/policyTemplates
 */
import { useAtomValue } from "@effect/atom-react";
import { domainApiReadPath } from "@repo/shared/domainApiRoutes";
import {
  PolicyTemplate as PolicyTemplateSchema,
  PolicyTemplateList as PolicyTemplateListSchema,
  type PolicyTemplateFromPolicy,
  type PolicyTemplateUpload,
} from "@repo/shared/policyTemplates";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { complianceQueryAtom } from "./complianceLive";
import { fetchComplianceJson as fetchJson } from "./complianceFetch";
import type { OrgProfileUnavailableReason } from "./orgProfile";
import { fileToBase64, messageFromBody, type UploadOutcome } from "./policies";

export type PolicyTemplate = typeof PolicyTemplateSchema.Type;
export type PolicyTemplateListSnapshot = typeof PolicyTemplateListSchema.Type;

const decodeList = Schema.decodeUnknownEffect(PolicyTemplateListSchema);
const decodeTemplate = Schema.decodeUnknownSync(PolicyTemplateSchema);

const templatesUrl = (organizationId: string, extra?: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policy-templates", { organizationId, ...extra });

const templatesAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchJson(
          {
            proxyUrl: templatesUrl(organizationId),
            apiPath: domainApiReadPath("policy-templates", { organizationId }),
          },
          decodeList,
        ),
  ).pipe(Atom.withLabel(`web-amoreg:policy-templates:${organizationId}`)),
);

export interface TemplateUploadInput {
  readonly file: File;
  readonly title: string;
  readonly description: string;
  readonly policyType: string;
  readonly documentKind: "policy" | "procedure";
  readonly jurisdictionSlugs: ReadonlyArray<string>;
}

export interface PolicyTemplatesView {
  readonly snapshot: PolicyTemplateListSnapshot | null;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly isPending: boolean;
  readonly refresh: () => void;
  readonly upload: (input: TemplateUploadInput) => Promise<UploadOutcome<PolicyTemplate>>;
  /** Save one of the organisation's policies as a template (a copy of its current file). */
  readonly saveFromPolicy: (
    input: PolicyTemplateFromPolicy,
  ) => Promise<UploadOutcome<PolicyTemplate>>;
  /** Resolves to an error message, or null once archived. */
  readonly archive: (templateId: string) => Promise<string | null>;
  /** Opens the server-proxied download, never the storage URL. */
  readonly download: (templateId: string) => void;
}

export function usePolicyTemplates(organizationId: string | null): PolicyTemplatesView {
  const atom = useMemo(() => templatesAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailable =
    data !== null && typeof data === "object" && "unavailable" in data
      ? (data.unavailable as OrgProfileUnavailableReason)
      : result._tag === "Failure"
        ? ("error" as const)
        : null;
  const snapshot =
    unavailable === null ? ((data ?? null) as PolicyTemplateListSnapshot | null) : null;

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const post = useCallback(
    async (url: string, body: unknown, failure: string): Promise<UploadOutcome<PolicyTemplate>> => {
      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          return { error: messageFromBody(await response.text().catch(() => ""), failure) };
        }
        const template = decodeTemplate(await response.json());
        refresh();
        return { error: null, value: template };
      } catch {
        return { error: failure };
      }
    },
    [refresh],
  );

  const upload = useCallback(
    async (input: TemplateUploadInput): Promise<UploadOutcome<PolicyTemplate>> => {
      if (!organizationId) return { error: "No organization selected." };
      const failure = "The template could not be uploaded.";
      let contentBase64: string;
      try {
        contentBase64 = await fileToBase64(input.file);
      } catch {
        return { error: failure };
      }
      const description = input.description.trim();
      const policyType = input.policyType.trim();
      return post(
        templatesUrl(organizationId),
        {
          filename: input.file.name,
          contentBase64,
          title: input.title.trim(),
          ...(description.length > 0 ? { description } : {}),
          ...(policyType.length > 0 ? { policyType } : {}),
          documentKind: input.documentKind,
          jurisdictionSlugs: [...input.jurisdictionSlugs],
        } satisfies PolicyTemplateUpload,
        failure,
      );
    },
    [organizationId, post],
  );

  const saveFromPolicy = useCallback(
    async (input: PolicyTemplateFromPolicy): Promise<UploadOutcome<PolicyTemplate>> => {
      if (!organizationId) return { error: "No organization selected." };
      return post(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policy-templates/from-policy", {
          organizationId,
        }),
        input,
        "The policy could not be saved as a template.",
      );
    },
    [organizationId, post],
  );

  const archive = useCallback(
    async (templateId: string): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(templatesUrl(organizationId, { templateId }), {
          method: "DELETE",
          credentials: "include",
        });
        if (!response.ok) {
          return messageFromBody(
            await response.text().catch(() => ""),
            "The template could not be archived.",
          );
        }
        refresh();
        return null;
      } catch {
        return "The template could not be archived.";
      }
    },
    [organizationId, refresh],
  );

  const download = useCallback(
    (templateId: string) => {
      if (!organizationId) return;
      window.open(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policy-templates/download", {
          organizationId,
          templateId,
        }),
        "_blank",
        "noopener",
      );
    },
    [organizationId],
  );

  return {
    snapshot,
    unavailableReason: unavailable,
    isPending: result.waiting,
    refresh,
    upload,
    saveFromPolicy,
    archive,
    download,
  };
}
